import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box, Card, CardContent, LinearProgress, Stack, Tooltip, Typography,
} from '@mui/material';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';

import { formatPercent, formatTonne } from '../../../shared/utils/number';

export default function CategoryCard({
  code,
  label,
  value,
  total,
  color,
  bg,
  shareLabel,
  compact = false,
  emissionLevel,
  emissionLevelLabel,
  hasData = true,
  noDataLabel,
}) {
  const theme = useTheme();
  const share = total > 0 ? (value / total) * 100 : 0;
  const levelStyles = {
    high: { Icon: TrendingUpRoundedIcon, color: theme.palette.error.main },
    medium: { Icon: RemoveRoundedIcon, color: theme.palette.warning.dark },
    low: { Icon: TrendingDownRoundedIcon, color: theme.palette.success.main },
  };
  const levelStyle = levelStyles[emissionLevel];

  return (
    <Card
      component="article"
      sx={{
        bgcolor: compact
          ? 'background.paper'
          : theme.palette.mode === 'dark' ? alpha(color, 0.1) : bg || alpha(color, 0.055),
        borderColor: alpha(color, theme.palette.mode === 'dark' ? 0.45 : 0.65),
        borderStyle: hasData ? 'solid' : 'dashed',
        height: '100%',
        opacity: hasData ? 1 : 0.68,
      }}
    >
      <CardContent sx={{ p: compact ? 1.75 : 2, '&:last-child': { pb: compact ? 1.75 : 2 } }}>
        <Stack spacing={compact ? 1 : 1}>
          <Box sx={{ minHeight: compact ? 42 : 46, position: 'relative', pr: levelStyle ? 4 : 0 }}>
            <Typography variant="overline" sx={{ color, display: 'block', fontSize: 14, fontWeight: 700, lineHeight: 1.25, textTransform: 'none', marginBottom: '8px' }}>
              {code}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 650, lineHeight: 1.35 }}>
              {label}
            </Typography>
            {levelStyle && (
              <Tooltip title={emissionLevelLabel || ''} arrow>
                <Box
                  component="span"
                  aria-label={emissionLevelLabel}
                  sx={{
                    alignItems: 'center',
                    bgcolor: alpha(levelStyle.color, 0.1),
                    borderRadius: '50%',
                    color: levelStyle.color,
                    display: 'inline-flex',
                    height: 26,
                    justifyContent: 'center',
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: 26,
                  }}
                >
                  <levelStyle.Icon sx={{ fontSize: 18 }} />
                </Box>
              </Tooltip>
            )}
          </Box>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
            <Typography
              sx={{ color, fontSize: 23, fontWeight: 700, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}
            >
              {formatTonne(value)}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>tCO₂e</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={hasData ? Math.min(share, 100) : 0}
            sx={{
              bgcolor: alpha(color, 0.13),
              borderRadius: 99,
              height: compact ? 4 : 5,
              '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 99 },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650 }}>
            {hasData ? (shareLabel || formatPercent(share)) : noDataLabel}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
